import { Modal, Flex, Group, Button } from '@mantine/core';
import { zodResolver, useForm } from '@mantine/form';
import { ApiErrorCode, ApiError } from 'src/error/ApiError';
import { GenericErrorModal } from 'src/error/GenericErrorModal';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import PageLoader from 'src/UI/loader/PageLoader';
import useUsers from '../hooks/useUsers';
import CreateUserForm, { CreateUserFormValues } from './CreateUserForm';
import { formSchema, isDuplicateEmail } from './CreateUserModal';
import { User } from '../types';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type EditUserModalProps = {
  user: User | null;
  onClose: () => void;
  onSave: (updatedUser: User) => Promise<void>;
  error?: unknown;
  isLoading?: boolean;
};

export default function EditUserModal({
  user,
  onClose,
  onSave,
  error,
  isLoading,
}: EditUserModalProps) {
  const { users } = useUsers();

  const form = useForm<CreateUserFormValues>({
    mode: 'controlled',
    initialValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      emailAddress: user?.emailAddress ?? '',
      userRole: user?.userRole ?? 'ADVISOR',
    },
    validate: values => {
      const zodErrors = zodResolver(formSchema)(values);
      return {
        ...zodErrors,
        emailAddress: !values.emailAddress
          ? null
          : values.emailAddress.toLowerCase() === user?.emailAddress?.toLowerCase()
            ? null
            : isDuplicateEmail(users, values.emailAddress)
              ? 'This email address is already in use.'
              : zodErrors.emailAddress,
      };
    },
    validateInputOnBlur: true,
  });

  if (!user) return null;

  const onEditUser = async (values: CreateUserFormValues) => {
    try {
      const updatedUser: User = values as User;
      updatedUser.id = user.id;
      await onSave(values as User);
      Mixpanel.track(MixpanelEvents.USER_UPDATED);
      onClose();
    } catch (e) {
      if (ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, e)) {
        form.setFieldError('emailAddress', e.message);
      }
      notifyAndLogError(
        'Failed to update user',
        'An error occurred while updating the user. Please try again.',
        e,
        { userId: user.id, userData: values },
      );
    }
  };

  if (error && !ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, error)) {
    return <GenericErrorModal opened={true} onClose={onClose} />;
  }

  return (
    <Modal opened={!!user} onClose={onClose} title="Edit user" centered>
      {isLoading ? (
        <PageLoader absolute={false} />
      ) : (
        <form onSubmit={form.onSubmit(values => onEditUser(values))}>
          <Flex direction="column" gap="md">
            <CreateUserForm form={form} />
          </Flex>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="filled-pill-black" disabled={!form.isValid()}>
              Save
            </Button>
          </Group>
        </form>
      )}
    </Modal>
  );
}
